/*
  ExerciseProgramWorksheetPreview — in-editor A4-landscape preview
  matching the "Client Workout Completion Record" worksheet layout
  served by GET /api/exercise-programs/{id}/pdf?layout=worksheet.

  Header (Client / Program / Precautions) → grid with 6 day columns
  (or configurable via program.worksheet_settings.days) → SOAP Session
  Notes section (pre-fills saved notes, plus blank ruled lines).

  When `editable` + `programId` are provided, each day cell becomes a
  tap-to-log input that persists via POST /api/exercise-programs/{id}/daily-log.
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

function CellInput({ programId, sectionIndex, exerciseId, dayIndex, initial, onSaved }) {
  const [reps, setReps] = useState(initial?.reps || "");
  const [weight, setWeight] = useState(initial?.weight || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setReps(initial?.reps || "");
    setWeight(initial?.weight || "");
  }, [initial?.reps, initial?.weight]);

  async function commit() {
    // Only fire when there's a change vs the initial state
    const initReps = initial?.reps || "";
    const initWt = initial?.weight || "";
    if (reps === initReps && weight === initWt) return;
    setSaving(true);
    try {
      await api.post(`/exercise-programs/${programId}/daily-log`, {
        section_index: sectionIndex,
        exercise_id: exerciseId,
        day_index: dayIndex,
        reps: reps || null,
        weight: weight || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
      onSaved?.();
    } catch (e) {
      toast.error("Couldn't save cell", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex items-center justify-center gap-0.5" onBlur={commit}>
      <input
        value={reps}
        onChange={(e) => setReps(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="reps"
        className="w-10 text-center bg-transparent border-b border-slate-300 focus:border-indigo-500 outline-none text-[9pt]"
        style={{ fontFamily: '"Courier New", monospace' }}
        data-testid={`ws-cell-reps-${sectionIndex}-${exerciseId}-${dayIndex}`}
      />
      <span className="text-slate-400 text-[9pt]">/</span>
      <input
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder="wt"
        className="w-10 text-center bg-transparent border-b border-slate-300 focus:border-indigo-500 outline-none text-[9pt]"
        style={{ fontFamily: '"Courier New", monospace' }}
        data-testid={`ws-cell-weight-${sectionIndex}-${exerciseId}-${dayIndex}`}
      />
      {saving && <Loader2 className="h-3 w-3 animate-spin text-indigo-500 ml-0.5" />}
      {!saving && saved && <Check className="h-3 w-3 text-emerald-600 ml-0.5" />}
    </div>
  );
}

export default function ExerciseProgramWorksheetPreview({
  program,
  programId,
  editable = false,
  onDailyLogSaved,
  pageOrientation = "landscape",
  colorMode = "bw",
}) {
  if (!program) return null;
  // Palette mirrors the backend `_render_worksheet_html` colour_mode
  // logic so the on-screen preview matches what the printer sees.
  // BW = no fills; Colour = light indigo tints on section headers.
  const isColor = colorMode === "color";
  const P = {
    dayHead:      isColor ? "bg-indigo-100 text-indigo-900" : "bg-white text-zinc-900",
    daySub:       isColor ? "bg-indigo-50 text-indigo-800"  : "bg-white text-zinc-700",
    headBg:       isColor ? "bg-slate-100"                  : "bg-white",
    sectionRow:   isColor
      ? "bg-indigo-50 text-indigo-950 border-y-2 border-indigo-700"
      : "bg-white text-zinc-900 border-y-2 border-zinc-900",
    soapTitle:    isColor ? "text-indigo-900 border-indigo-900" : "text-zinc-900 border-zinc-900",
    soapEntryL:   isColor ? "border-l-indigo-600" : "border-l-zinc-900",
  };
  const days =
    (program.worksheet_settings?.days &&
      program.worksheet_settings.days >= 1 &&
      program.worksheet_settings.days <= 12)
      ? program.worksheet_settings.days
      : 6;
  const showRpe = !!program.worksheet_settings?.show_rpe_column;
  const tailCols = days + (showRpe ? 1 : 0);

  // Pain-score rows — Pre pinned above the exercises, Post below.
  // Entries map chronologically to day columns so col i shows
  // session i's score. Blanks render as empty for hand-fill.
  const painEnabled = !!program.pain_tracking_enabled;
  const painEntries = painEnabled
    ? [...(program.pain_log || [])].sort(
        (a, b) => (a.date || "").localeCompare(b.date || ""),
      )
    : [];
  function painCell(kind, i) {
    const entry = painEntries[i];
    const v = entry && entry[kind] != null ? entry[kind] : null;
    return (
      <td
        key={`p-${kind}-${i}`}
        className="border border-slate-400 px-1 py-1 text-center text-[9pt] font-mono font-semibold"
        style={{ background: kind === "pre" ? "#fff1f2" : "#f0fdf4" }}
      >
        {v == null ? "" : v}
      </td>
    );
  }
  function noteCell(i) {
    const entry = painEntries[i];
    const note = (entry && entry.note) || "";
    return (
      <td
        key={`p-note-${i}`}
        className="border border-slate-400 px-1 py-1 text-left text-[8pt] text-slate-700 align-top"
        style={{ background: "#f0fdf4" }}
      >
        {note}
      </td>
    );
  }
  function painRow(kind) {
    if (!painEnabled) return null;
    const label = kind === "pre"
      ? "Pre-session pain (0-10)"
      : "Post-session pain (0-10)";
    return (
      <tr key={`pain-${kind}`} data-testid={`ep-preview-pain-row-${kind}`}>
        <td
          colSpan={3}
          className="border border-slate-400 px-2 py-1 text-right text-[8pt] uppercase tracking-wide font-bold"
          style={{
            background: kind === "pre" ? "#fff1f2" : "#f0fdf4",
            color: kind === "pre" ? "#9f1239" : "#065f46",
          }}
        >
          {label}
        </td>
        {Array.from({ length: days }).map((_, i) => painCell(kind, i))}
        {showRpe && (
          <td
            className="border border-slate-400 px-1 py-1 text-center text-[9pt] text-slate-400"
            style={{ background: kind === "pre" ? "#fff1f2" : "#f0fdf4" }}
          />
        )}
      </tr>
    );
  }
  function noteRow() {
    if (!painEnabled) return null;
    return (
      <tr key="pain-note" data-testid="ep-preview-pain-row-note">
        <td
          colSpan={3}
          className="border border-slate-400 px-2 py-1 text-right text-[8pt] font-semibold"
          style={{ background: "#f0fdf4", color: "#065f46" }}
        >
          Session note (cause / trigger)
        </td>
        {Array.from({ length: days }).map((_, i) => noteCell(i))}
        {showRpe && (
          <td
            className="border border-slate-400 px-1 py-1"
            style={{ background: "#f0fdf4" }}
          />
        )}
      </tr>
    );
  }

  const precautions =
    program.precautions_pathologies ||
    (program.precautions || []).join("; ") ||
    "";

  // ---------------------------------------------------------------------
  // Page-break estimator — surfaces a visible marker row wherever the
  // print engine will start a new page in the PDF. Rows are weighted by
  // approximate rendered height (section headings + exercise rows +
  // "empty section" placeholders). Budget varies by orientation: A4
  // landscape has ~155mm of usable body height before the first break,
  // A4 portrait ~245mm. Subsequent pages start with no header, so the
  // subsequent budget is wider.
  // Row heights (approx, at 10pt): section heading 5mm, exercise row
  // 7mm, empty-section placeholder 6mm.
  // ---------------------------------------------------------------------
  const isLandscape = pageOrientation !== "portrait";
  const FIRST_PAGE_BUDGET_MM = isLandscape ? 155 : 245;
  const NEXT_PAGE_BUDGET_MM = isLandscape ? 175 : 265;
  const SECTION_HEADING_MM = 5;
  const EXERCISE_ROW_MM = 7;
  const EMPTY_SECTION_MM = 6;
  let pageBudget = FIRST_PAGE_BUDGET_MM;
  let pageUsed = 0;
  let pageNumber = 1;
  // Injects a visible break row into ``rows`` before the current entry
  // when the running total would exceed the remaining budget on this
  // page. Called just before pushing each section-heading / exercise row.
  const tryBreakBefore = (rowHeightMm, keyPrefix) => {
    // Ensure we don't insert a break mid-section: a section-heading
    // that overflows the page is what we want to break before, and an
    // exercise that lives on the same page as its heading should stick
    // with it (avoid orphaning). Caller is responsible for the timing;
    // this helper only enforces the budget.
    if (pageUsed + rowHeightMm > pageBudget) {
      pageNumber += 1;
      rows.push(
        <tr key={`pb-${keyPrefix}-${pageNumber}`}
            className="page-break-marker"
            data-testid={`worksheet-page-break-${pageNumber}`}>
          <td colSpan={3 + tailCols}
              className="text-center text-[8pt] uppercase tracking-widest text-indigo-800 bg-indigo-50/70 border-y-2 border-dashed border-indigo-400 py-1">
            ↓ Page {pageNumber} starts here ↓
          </td>
        </tr>,
      );
      pageBudget = NEXT_PAGE_BUDGET_MM;
      pageUsed = 0;
    }
  };

  const rows = [];
  (program.sections || []).forEach((s, si) => {
    tryBreakBefore(SECTION_HEADING_MM, `s-${si}`);
    pageUsed += SECTION_HEADING_MM;
    rows.push(
      <tr key={`h-${si}`} className={`${P.sectionRow} uppercase`}>
        <td colSpan={3 + tailCols} className="px-2 py-1 text-[8pt] tracking-wide font-semibold">
          {s.label || s.key || "Section"}
        </td>
      </tr>,
    );
    const exs = s.exercises || [];
    if (exs.length === 0) {
      pageUsed += EMPTY_SECTION_MM;
      rows.push(
        <tr key={`e-${si}-none`}>
          <td colSpan={3 + tailCols} className="italic text-slate-400 text-center py-1">
            —
          </td>
        </tr>,
      );
    }
    exs.forEach((ex, ei) => {
      tryBreakBefore(EXERCISE_ROW_MM, `e-${si}-${ei}`);
      pageUsed += EXERCISE_ROW_MM;
      // Set-groups (structured multi-weight) take priority over the
      // flat sets/reps/load fields when present. Render each group
      // on its own line inside the dose + weight cells to mirror the
      // PDF renderer exactly. If the flat sets/reps/load carries
      // data that ISN'T already duplicated by the first set-group,
      // prepend it as its own line so a practitioner setting both
      // flat and breakdown sees both rows print.
      const setGroups = (ex.set_groups || []).filter(
        (g) => g && (g.sets || g.reps || g.load),
      );
      let doseRows;
      if (setGroups.length > 0) {
        const flatSets = (ex.sets || "").toString().trim();
        const flatReps = (ex.reps || "").toString().trim();
        const flatLoad = (ex.load || "").toString().trim();
        const first = setGroups[0] || {};
        const dupFirst = flatSets === (first.sets || "").toString().trim()
          && flatReps === (first.reps || "").toString().trim()
          && flatLoad === (first.load || "").toString().trim();
        doseRows = [];
        if ((flatSets || flatReps || flatLoad) && !dupFirst) {
          doseRows.push({ sets: flatSets, reps: flatReps, load: flatLoad });
        }
        doseRows.push(...setGroups);
      }
      const setsReps = doseRows
        ? doseRows.map((g) => (g.sets && g.reps ? `${g.sets} × ${g.reps}` : (g.sets || g.reps || ""))).join("\n")
        : (ex.reps
            ? `${ex.sets || ""}${ex.sets ? " × " : ""}${ex.reps}`
            : ex.sets || "");
      // Weight column mirrors the printed PDF (_format_load_cell):
      // combine load + band + spring on each dose row so Reformer
      // spring settings and TheraBand colours appear alongside weight
      // in kg, then fold in the remaining non-dose descriptors (hold,
      // tempo, rest, RPE, frequency, side, apparatus) in "<value>
      // <label>" form so the worksheet actually surfaces them.
      // Format: "5kg, Purple (Band), 2.0 (Spring), 10s hold, slow
      // tempo, 60s rest, RPE 7, 3x/wk freq, left side, reformer app".
      const _formatLoadCell = (load, band, spring, extras = null) => {
        const parts = [];
        const l = String(load ?? "").trim();
        if (l) parts.push(l);
        const b = String(band ?? "").trim();
        if (b) parts.push(`${b} (Band)`);
        const s = String(spring ?? "").trim();
        if (s) parts.push(`${s} (Spring)`);
        if (extras) {
          for (const [key, label] of [
            ["hold", "hold"],
            ["tempo", "tempo"],
            ["rest", "rest"],
            ["frequency", "freq"],
            ["side", "side"],
            ["apparatus", "app"],
          ]) {
            const v = String(extras[key] ?? "").trim();
            if (v) parts.push(`${v} ${label}`);
          }
          const rpe = String(extras.rpe ?? "").trim();
          if (rpe) parts.push(`RPE ${rpe}`);
        }
        return parts.join(", ");
      };
      // Non-dose descriptors live at the exercise level (not per set-
      // group), so we only fold them into the FIRST weight row —
      // otherwise a 3-row set breakdown would repeat "10s hold" three
      // times.
      const exExtras = {
        hold: ex.hold, tempo: ex.tempo, rest: ex.rest, rpe: ex.rpe,
        frequency: ex.frequency, side: ex.side, apparatus: ex.apparatus,
      };
      const weightCell = doseRows
        ? doseRows.map((g, gi) => _formatLoadCell(
            g.load, g.band ?? ex.band, g.spring ?? ex.spring,
            gi === 0 ? exExtras : null,
          )).join("\n")
        : _formatLoadCell(ex.load, ex.band, ex.spring, exExtras);
      const log = ex.daily_log || [];
      const dayCells = [];
      for (let i = 0; i < days; i++) {
        const entry = log[i];
        if (editable && programId && ex.id) {
          dayCells.push(
            <td key={`d-${i}`} className="border border-slate-400 px-1 py-1 align-top">
              <CellInput
                programId={programId}
                sectionIndex={si}
                exerciseId={ex.id}
                dayIndex={i}
                initial={entry}
                onSaved={onDailyLogSaved}
              />
              {entry?.symptoms && (
                <div className="text-[7.5pt] text-amber-700 mt-0.5 text-center">
                  {entry.symptoms}
                </div>
              )}
            </td>,
          );
        } else {
          const reps = entry?.reps || "";
          const wt = entry?.weight || "";
          const has = reps || wt;
          dayCells.push(
            <td
              key={`d-${i}`}
              className="border border-slate-400 text-center text-[9pt] px-1 py-1 whitespace-nowrap"
              style={{ fontFamily: '"Courier New", monospace', color: "#475569" }}
            >
              {has ? `${reps}/${wt}` : ""}
              {entry?.symptoms && (
                <div className="text-[7.5pt] text-amber-700 mt-0.5">
                  {entry.symptoms}
                </div>
              )}
            </td>,
          );
        }
      }
      rows.push(
        <tr key={`ex-${si}-${ei}`}>
          <td className="border border-slate-400 px-2 py-1 text-[9pt] w-[20%]">
            {ex.name || ""}
          </td>
          <td className="border border-slate-400 px-1 py-1 text-center text-[9pt] w-[9%] text-slate-700 whitespace-pre">
            {setsReps}
          </td>
          <td className="border border-slate-400 px-1 py-1 text-center text-[9pt] w-[7%] text-slate-700 whitespace-normal break-words leading-tight">
            {weightCell}
          </td>
          {dayCells}
          {showRpe && (
            <td className="border border-slate-400 px-1 py-1 text-center text-[9pt] w-[5%] text-slate-500 font-mono">
              &nbsp;
            </td>
          )}
        </tr>,
      );
    });
  });

  // In editable mode, disable the outer transform-scale so inputs are
  // full-size and easily tappable/tabable.
  // Non-editable preview scales the canvas down to fit alongside the
  // editor; canvas dimensions flip when the operator chooses portrait
  // so the visible page-break markers line up with what actually prints.
  const outerStyle = editable
    ? { width: "100%", padding: "10mm 8mm", boxSizing: "border-box" }
    : {
        width: isLandscape ? "297mm" : "210mm",
        minHeight: isLandscape ? "210mm" : "297mm",
        padding: "12mm 10mm",
        transform: "scale(0.5)",
        transformOrigin: "top center",
        marginBottom: "-40%",
        boxSizing: "border-box",
      };

  return (
    <div
      className="a4-worksheet-preview bg-zinc-100 h-full overflow-auto p-3"
      data-testid="ep-worksheet-preview"
    >
      <div
        className="mx-auto bg-white shadow-md text-[10pt] text-zinc-900"
        style={outerStyle}
      >
        <h1
          className="text-[16pt] m-0 mb-2 uppercase font-bold"
          style={{ letterSpacing: "0.5pt" }}
        >
          Client Workout Completion Record
        </h1>

        <table
          className="w-full border-collapse mb-2"
          style={{ border: "1pt solid #cbd5e1" }}
        >
          <tbody>
            <tr>
              <td className="border border-slate-300 p-2 w-1/2 align-top">
                <div className="text-[8pt] uppercase tracking-wide text-slate-600 font-semibold">
                  Client name
                </div>
                <div className="text-[11pt] text-slate-900 min-h-[14pt]" data-testid="worksheet-client-name">
                  {(program.client_name || "").trim() || program.client_email || ""}
                </div>
              </td>
              <td className="border border-slate-300 p-2 w-1/2 align-top">
                <div className="text-[8pt] uppercase tracking-wide text-slate-600 font-semibold">
                  Program / service
                </div>
                <div className="text-[11pt] text-slate-900 min-h-[14pt]">
                  {program.title || "Exercise program"}
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan={2} className="border border-slate-300 p-2 align-top">
                <div className="text-[8pt] uppercase tracking-wide text-slate-600 font-semibold">
                  Precautions / pathologies
                </div>
                <div className="text-[11pt] text-slate-900 min-h-[14pt]">
                  {precautions || "\u00A0"}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="italic text-[9pt] text-slate-600 mb-2">
          {editable
            ? "Tap any Day cell to log actual reps and weight — auto-saves on blur or Enter."
            : "Daily cells: record completion, actual weight / repetitions, symptoms or brief modifications."}
        </div>

        <table className="w-full border-collapse mb-3">
          <thead>
            <tr className={`${P.headBg} uppercase text-[8.5pt] whitespace-nowrap`}>
              <th className="border border-slate-400 py-1">Exercise name</th>
              <th className="border border-slate-400 py-1">Sets/Reps</th>
              <th className="border border-slate-400 py-1">Weight</th>
              {Array.from({ length: days }).map((_, i) => (
                <th
                  key={`dh-${i}`}
                  className={`border border-slate-400 py-1 ${P.dayHead}`}
                >
                  Day {i + 1}
                </th>
              ))}
              {showRpe && (
                <th className={`border border-slate-400 py-1 ${P.dayHead}`}>RPE</th>
              )}
            </tr>
            {!editable && (
              <tr>
                <th className="border border-slate-400"></th>
                <th className="border border-slate-400"></th>
                <th className="border border-slate-400"></th>
                {Array.from({ length: days }).map((_, i) => (
                  <th
                    key={`ds-${i}`}
                    className={`border border-slate-400 py-1 text-[8pt] font-normal whitespace-nowrap ${P.daySub}`}
                    style={{ fontFamily: '"Courier New", monospace' }}
                  >
                    &nbsp;
                  </th>
                ))}
                {showRpe && (
                  <th
                    className={`border border-slate-400 py-1 text-[8pt] font-normal ${P.daySub}`}
                    style={{ fontFamily: '"Courier New", monospace' }}
                  >
                    0-10
                  </th>
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {painRow("pre")}
            {rows.length > 0 ? rows : (
              <tr>
                <td
                  colSpan={3 + tailCols}
                  className="italic text-slate-400 text-center py-2"
                >
                  No exercises prescribed yet.
                </td>
              </tr>
            )}
            {painRow("post")}
            {noteRow()}
          </tbody>
        </table>

        <h2
          className="text-[12pt] uppercase mt-3 mb-1 pb-0.5 font-semibold"
          style={{
            color: isColor ? "#312e81" : "#0f172a",
            borderBottom: `2px solid ${isColor ? "#312e81" : "#0f172a"}`,
            letterSpacing: "0.4pt",
          }}
        >
          Workout SOAP Notes / Session Updates
        </h2>
        <div className="text-[9pt] text-slate-600 mb-1">
          Record the session date and relevant subjective, objective,
          assessment and plan notes below.
        </div>

        {(program.session_notes || []).map((n, ni) => (
          <div
            key={ni}
            className="border border-slate-300 p-2 mb-1"
            style={{ borderLeft: `3pt solid ${isColor ? "#6366f1" : "#0f172a"}` }}
          >
            <div
              className="text-[8pt] uppercase tracking-wide font-semibold mb-1"
              style={{ color: isColor ? "#4338ca" : "#0f172a" }}
            >
              {(n.date || "").slice(0, 10)}
            </div>
            {[
              ["subjective", "S"],
              ["objective", "O"],
              ["assessment", "A"],
              ["plan", "P"],
            ].map(([k, lbl]) =>
              n[k] ? (
                <div key={k} className="text-[9.5pt] text-slate-900 my-0.5">
                  <b>{lbl}:</b> {n[k]}
                </div>
              ) : null,
            )}
            {n.note && !n.subjective && !n.objective && !n.assessment && !n.plan && (
              <div className="text-[9.5pt] text-slate-900">{n.note}</div>
            )}
          </div>
        ))}
        {!editable && (
          <div className="mt-1">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={`bl-${i}`}
                style={{ borderBottom: "1pt solid #cbd5e1", height: "16pt" }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
